import { supabase } from '../src/lib/supabaseClient';

export type FeatureType = 
  | 'render' 
  | 'reconstruct' 
  | 'upscale' 
  | 'tone_mapping' 
  | 'light_reconstruct' 
  | 'text_reconstruct' 
  | 'physical_reconstruct';

/**
 * 记录功能使用
 * 用于统计每日成本和收入
 * 
 * @param featureType 功能类型
 * @returns 是否记录成功
 */
export async function recordFeatureUsage(featureType: FeatureType): Promise<boolean> {
  try {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    
    const { error } = await supabase.rpc('record_feature_usage', {
      p_feature_type: featureType,
      p_date: today
    });

    if (error) {
      console.error('[usageService] 记录功能使用失败:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('[usageService] 记录功能使用异常:', err);
    return false;
  }
}

/**
 * 获取每日收支报表
 * 
 * @param startDate 开始日期 YYYY-MM-DD
 * @param endDate 结束日期 YYYY-MM-DD
 * @returns 报表数据
 */
export async function getDailyReport(startDate: string, endDate: string) {
  try {
    const { data, error } = await supabase.rpc('get_daily_report', {
      p_start_date: startDate,
      p_end_date: endDate
    });

    if (error) {
      console.error('[usageService] 获取日报表失败:', error);
      return null;
    }

    return data;
  } catch (err) {
    console.error('[usageService] 获取日报表异常:', err);
    return null;
  }
}

/**
 * 获取指定日期的功能成本明细
 * 
 * @param date 日期 YYYY-MM-DD
 * @returns 功能成本明细
 */
export async function getFeatureCostReport(date: string) {
  try {
    const { data, error } = await supabase.rpc('get_feature_cost_report', {
      p_date: date
    });

    if (error) {
      console.error('[usageService] 获取功能成本报表失败:', error);
      return null;
    }

    return data;
  } catch (err) {
    console.error('[usageService] 获取功能成本报表异常:', err);
    return null;
  }
}

/**
 * 刷新指定日期的收入统计
 * 从credits_ledger重新计算当日收入
 * 
 * @param date 日期 YYYY-MM-DD，默认今天
 */
export async function refreshDailyRevenue(date?: string): Promise<boolean> {
  try {
    const targetDate = date || new Date().toISOString().split('T')[0];
    
    const { error } = await supabase.rpc('refresh_daily_revenue_stats', {
      p_date: targetDate
    });

    if (error) {
      console.error('[usageService] 刷新收入统计失败:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('[usageService] 刷新收入统计异常:', err);
    return false;
  }
}

// 功能显示名称映射
export const FEATURE_DISPLAY_NAMES: Record<FeatureType, string> = {
  'render': '灯光图',
  'reconstruct': '光影修改图',
  'upscale': 'AI放大',
  'tone_mapping': '光影映射',
  'light_reconstruct': '光影重构',
  'text_reconstruct': '文本重构',
  'physical_reconstruct': '物理重构'
};

// 功能单次积分消耗
export const FEATURE_CREDIT_COST: Record<FeatureType, number> = {
  'render': 15,
  'reconstruct': 15,
  'upscale': 10,
  'tone_mapping': 10,
  'light_reconstruct': 10,
  'text_reconstruct': 10,
  'physical_reconstruct': 10
};

// 单次成本（元）- 应与feature_costs表一致
export const FEATURE_UNIT_COST: Record<FeatureType, number> = {
  'render': 0.75,
  'reconstruct': 0.25,
  'upscale': 0.20,
  'tone_mapping': 0.20,
  'light_reconstruct': 0.20,
  'text_reconstruct': 0.20,
  'physical_reconstruct': 0.20
};
